import React, { useState } from 'react';
import { Bell, Check, CheckCheck, Package, ShieldCheck, MessageSquare, Trash2, X } from 'lucide-react';
import { AppNotification } from '../types';

interface NotificationCenterProps {
  notifications: AppNotification[];
  onMarkAsRead: (notificationId: string) => Promise<void> | void;
  onMarkAllAsRead: () => Promise<void> | void;
  onDeleteNotification?: (notificationId: string) => Promise<void> | void;
  onDeleteAllNotifications?: () => Promise<void> | void;
}

export const NotificationCenter: React.FC<NotificationCenterProps> = ({
  notifications,
  onMarkAsRead,
  onMarkAllAsRead,
  onDeleteNotification,
  onDeleteAllNotifications
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission>(
    typeof window !== 'undefined' && 'Notification' in window ? Notification.permission : 'default'
  );

  const unreadCount = notifications.filter(n => !n.read).length;

  // Request Web Push Notification Permission
  const handleRequestPushPermission = async () => {
    if (typeof window !== 'undefined' && 'Notification' in window) {
      const result = await Notification.requestPermission();
      setPermission(result);
      if (result === 'granted') {
        new Notification('Do Japão Forwarding 📦', {
          body: 'Notificações Push ativadas com sucesso! Você receberá atualizações quando seus pacotes chegarem.',
          icon: '/imagens/icon.png'
        });
      }
    }
  };

  const handleDelete = (e: React.MouseEvent, notificationId: string) => {
    e.stopPropagation();
    if (onDeleteNotification) {
      onDeleteNotification(notificationId);
    }
  };

  const handleDeleteAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDeleteAllNotifications) {
      onDeleteAllNotifications();
    }
  };

  return (
    <div className="relative">
      
      {/* Bell Button */}
      <button
        id="btn-notification-bell"
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2.5 rounded-xl text-slate-300 hover:text-white hover:bg-white/10 transition-colors cursor-pointer"
        title="Notificações Push"
        aria-label="Abrir central de notificações"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute top-1.5 right-1.5 w-4 h-4 bg-[#E63946] text-white text-[10px] font-black rounded-full flex items-center justify-center animate-pulse">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Notification Dropdown Panel */}
      {isOpen && (
        <div 
          id="notification-dropdown-panel"
          className="absolute right-0 mt-3 w-80 sm:w-96 bg-white rounded-3xl shadow-2xl border border-slate-200 z-50 overflow-hidden animate-fade-in text-slate-800"
        >
          
          {/* Header */}
          <div className="bg-[#1D3557] text-white p-4 flex justify-between items-center">
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4 text-[#E63946]" />
              <h4 className="font-extrabold text-sm">Notificações da Suíte</h4>
              {unreadCount > 0 && (
                <span className="px-2 py-0.5 bg-[#E63946] text-white text-[10px] font-black rounded-full">
                  {unreadCount} novas
                </span>
              )}
            </div>
            
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  id="btn-mark-all-notifications-read"
                  type="button"
                  onClick={onMarkAllAsRead}
                  className="text-[11px] font-bold text-slate-300 hover:text-white flex items-center gap-1 cursor-pointer transition-colors"
                  title="Marcar todas como lidas"
                >
                  <CheckCheck className="w-3.5 h-3.5" />
                  <span>Marcar lidas</span>
                </button>
              )}
              {notifications.length > 0 && onDeleteAllNotifications && (
                <button
                  id="btn-clear-all-notifications"
                  type="button"
                  onClick={handleDeleteAll}
                  className="text-[11px] font-bold text-red-300 hover:text-red-100 flex items-center gap-1 cursor-pointer transition-colors ml-1"
                  title="Excluir todas as notificações"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  <span>Limpar todas</span>
                </button>
              )}
              <button 
                id="btn-close-notification-panel"
                type="button"
                onClick={() => setIsOpen(false)} 
                className="text-slate-300 hover:text-white cursor-pointer ml-1 p-1 rounded-lg hover:bg-white/10 transition-colors"
                aria-label="Fechar"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Web Push Banner request if not granted */}
          {permission !== 'granted' && (
            <div className="p-3 bg-blue-50 border-b border-blue-100 flex justify-between items-center text-xs">
              <span className="text-blue-900 font-semibold">Ativar Notificações Push no Navegador?</span>
              <button
                type="button"
                onClick={handleRequestPushPermission}
                className="px-2.5 py-1 bg-[#1D3557] text-white font-bold rounded-lg text-[10px] cursor-pointer hover:bg-slate-800 transition-colors"
              >
                Ativar
              </button>
            </div>
          )}

          {/* Notifications List */}
          <div className="max-h-80 overflow-y-auto divide-y divide-slate-100">
            {notifications.length === 0 ? (
              <div className="p-8 text-center text-slate-400 space-y-1">
                <Bell className="w-8 h-8 mx-auto stroke-1 text-slate-300" />
                <p className="text-xs font-semibold">Nenhuma notificação recente.</p>
              </div>
            ) : (
              notifications.map((notif) => (
                <div
                  key={notif.id}
                  id={`notification-item-${notif.id}`}
                  onClick={() => onMarkAsRead(notif.id)}
                  className={`p-3.5 transition-colors cursor-pointer flex items-start gap-3 group relative ${
                    notif.read ? 'bg-white hover:bg-slate-50' : 'bg-blue-50/50 hover:bg-blue-50/80 font-medium'
                  }`}
                >
                  <div className="p-2 bg-slate-100 rounded-xl h-fit flex-shrink-0 text-[#1D3557] mt-0.5">
                    {notif.type === 'package_status' && <Package className="w-4 h-4 text-emerald-600" />}
                    {notif.type === 'package_new' && <Package className="w-4 h-4 text-blue-600" />}
                    {notif.type === 'shipment_status' && <ShieldCheck className="w-4 h-4 text-amber-600" />}
                    {notif.type === 'support_reply' && <MessageSquare className="w-4 h-4 text-purple-600" />}
                  </div>

                  <div className="flex-1 min-w-0 space-y-0.5">
                    <div className="flex justify-between items-start gap-2">
                      <h5 className="text-xs font-extrabold text-[#1D3557] truncate">{notif.title}</h5>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        {!notif.read && (
                          <span className="w-2 h-2 bg-[#E63946] rounded-full mt-1" title="Não lida" />
                        )}
                        {onDeleteNotification && (
                          <button
                            id={`btn-delete-notification-${notif.id}`}
                            type="button"
                            onClick={(e) => handleDelete(e, notif.id)}
                            className="p-1 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors opacity-70 group-hover:opacity-100 cursor-pointer"
                            title="Excluir notificação permanentemente"
                            aria-label={`Excluir notificação: ${notif.title}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                    <p className="text-[11px] text-slate-600 leading-snug break-words">{notif.message}</p>
                    <span className="text-[9px] text-slate-400 font-mono block pt-1">
                      {new Date(notif.createdAt).toLocaleDateString([], { day: '2-digit', month: '2-digit' })} • {new Date(notif.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>

        </div>
      )}

    </div>
  );
};

export default NotificationCenter;
